import React from "react";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link, useParams } from "react-router-dom";
import {
  Calendar,
  Clock,
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  HelpCircle,
} from "lucide-react";
import { BLOG_POSTS } from "@/lib/blogPosts";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BlogArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-background font-body">
        <SEOHead
          title="Article Not Found"
          description="The requested article could not be found."
          canonical="https://www.jnnhomes.com/blog"
          noIndex
        />
        <Navbar />
        <main className="pt-32 pb-16 text-center">
          <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-primary hover:underline">
            ← Back to all articles
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  const scrollToForm = () => {
    window.location.href = "/#lead-form";
  };

  return (
    <div className="min-h-screen bg-background font-body">
      <SEOHead
        title={post.title}
        description={post.excerpt}
        canonical={`https://www.jnnhomes.com/blog/${post.slug}`}
        ogImage={post.image}
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: post.image,
            datePublished: post.date,
            author: { "@type": "Person", name: "Jason Nguyen" },
            publisher: {
              "@type": "Organization",
              name: "JNN Homes",
              url: "https://www.jnnhomes.com",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.jnnhomes.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Resources",
                item: "https://www.jnnhomes.com/blog",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: post.title,
                item: `https://www.jnnhomes.com/blog/${post.slug}`,
              },
            ],
          },
          ...(post.faq && post.faq.length
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: post.faq.map((f) => ({
                    "@type": "Question",
                    name: f.question,
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: f.answer,
                    },
                  })),
                },
              ]
            : []),
        ]}
      />
      <Navbar />

      <main className="pt-28 pb-16">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 max-w-3xl">
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-8">
            <Link to="/" className="hover:text-primary">
              Home
            </Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-primary">
              Resources
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground truncate">{post.category}</span>
          </nav>
        </div>

        {/* Hero image */}
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden mb-10 shadow-lg">
            <img
              src={post.image}
              alt={post.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Article body */}
        <article className="container mx-auto px-4 max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">
            {post.category}
          </span>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground mt-3 mb-6 leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-10 pb-6 border-b border-border">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />{" "}
              {new Date(post.date).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> {post.readTime}
            </span>
          </div>

          <div className="prose prose-lg max-w-none space-y-6">
            {post.content.map((para, i) => (
              <p key={i} className="text-lg leading-relaxed text-foreground/90">
                {para}
              </p>
            ))}
          </div>

          {/* FAQ */}
          {post.faq && post.faq.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center gap-2 mb-6">
                <HelpCircle className="w-5 h-5 text-primary" />
                <h2 className="text-2xl font-heading font-bold text-foreground">
                  Frequently Asked Questions
                </h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {post.faq.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-base font-body font-semibold text-foreground hover:text-primary">
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-base text-muted-foreground leading-relaxed">
                      {f.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 p-8 md:p-10 rounded-2xl bg-primary/5 border border-primary/20 text-center">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-3">
              Own a Property in VA or DC?
            </h2>
            <p className="text-muted-foreground mb-6">
              See how JNN Homes can help manage your short-term rental and
              maximize your income.
            </p>
            <Button
              onClick={scrollToForm}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-8 py-6 rounded-full text-lg"
            >
              See My Airbnb Income
            </Button>
          </div>
        </article>

        {/* Related */}
        <div className="container mx-auto px-4 max-w-6xl mt-20">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-heading font-bold">
              Related Articles
            </h2>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:gap-2 transition-all"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group flex flex-col rounded-2xl overflow-hidden border border-border/60 shadow-sm hover:shadow-xl transition-all bg-card"
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <span className="text-xs font-bold uppercase tracking-widest text-primary">
                    {p.category}
                  </span>
                  <h3 className="text-base font-heading font-bold mt-2 leading-snug">
                    {p.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogArticlePage;
